import type {
  AccountMoveFragment,
  BankAccountMoveDocumentInput,
  BankDocumentTypeEnum,
  Currency,
  Scalars,
} from "@src/__generated__/graphql";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { convertToWorkspaceCurrency } from "@src/utils/currency";
import { toApiDate } from "@src/utils/dates";
import { currency, denominate, nominate } from "@src/utils/formatters";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { required } from "@src/utils/forms/validators";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import type { DateRange } from "@src/utils/types";
import { FieldState, FormState } from "formstate";
import { upperFirst } from "lodash";
import { action, computed, makeObservable } from "mobx";

type FormFields = {
  amount: string;
  date: Date | undefined;
  documentId: Scalars["ID"]["output"];
  documentType: BankDocumentTypeEnum;
  exchangeRate: string;
};
export class SelectedDocument extends FormState<TToFieldStates<FormFields>> {
  isEditEnabled = new BooleanState(false);

  constructor(
    fields: FormFields,
    public currency: Currency,
    public documentNo: string,
    private selectedMoveCurrency: AccountMoveFragment["currency"] | undefined,
  ) {
    super({
      date: new FieldState(fields.date).validators(required),
      amount: new FieldState(fields.amount).validators(required),
      documentId: new FieldState(fields.documentId),
      documentType: new FieldState(fields.documentType),
      exchangeRate: new FieldState(
        nominate(
          Number(fields.exchangeRate),
          EXCHANGE_RATE_DENOMINATION,
        ).toString(),
      ).validators(required),
    });
    makeObservable(this, {
      hasForeignCurrency: computed,
      denominatedAmountInMoveCurrency: computed,
      mutationInput: computed,
      documentTypeReadable: computed,
      nominatedExchangeRate: computed,
      formattedExchangeRate: computed,
      setDate: action.bound,
      handleAmountChange: action.bound,
      handleEditCancel: action.bound,
      handleEditSave: action.bound,
    });
  }

  get hasForeignCurrency(): boolean {
    return this.currency.id !== this.selectedMoveCurrency?.id;
  }

  get exchangeRatePrefix() {
    if (!this.selectedMoveCurrency) return null;
    return `${currency.formatByCurrency(
      // NOTE: 100 so we get 1 of workspace currency
      100,
      this.selectedMoveCurrency,
      {
        fractionDigits: 0,
      },
    )} =`;
  }

  get nominatedExchangeRate() {
    return denominate(
      Number(this.$.exchangeRate.value),
      this.currency.denomination,
    );
  }

  get formattedExchangeRate(): string | null {
    if (!this.selectedMoveCurrency) return null;
    return currency.formatByCurrency(
      this.nominatedExchangeRate,
      this.selectedMoveCurrency,
    );
  }

  get denominatedAmountInMoveCurrency(): number {
    return convertToWorkspaceCurrency(Number(this.$.amount.$), {
      denomination: this.selectedMoveCurrency?.denomination,
      exchange_rate: denominate(
        Number(this.$.exchangeRate.value),
        EXCHANGE_RATE_DENOMINATION,
      ),
    });
  }

  get amountInputValue(): string {
    if (this.$.amount.value === "") return "";
    return nominate(
      Number(this.$.amount.value),
      this.currency.denomination,
    ).toString();
  }

  get mutationInput(): BankAccountMoveDocumentInput {
    return {
      amount: Number(this.$.amount.$),
      amount_in_move_currency: Math.round(this.denominatedAmountInMoveCurrency),
      date: toApiDate(this.$.date.$ ? this.$.date.$ : new Date()),
      document_id: this.$.documentId.$,
      document_type: this.$.documentType.$,
      exchange_rate: denominate(
        Number(this.$.exchangeRate.$),
        EXCHANGE_RATE_DENOMINATION,
      ),
    };
  }

  get documentTypeReadable(): string {
    return upperFirst(this.$.documentType.$.toLowerCase()).replaceAll("_", " ");
  }

  setDate(dateRange: DateRange | undefined) {
    this.$.date.onChange(dateRange?.start);
  }

  handleAmountChange(newValue: string) {
    this.$.amount.onChange(
      denominate(Number(newValue), this.currency.denomination).toString(),
    );
  }

  handleEditCancel() {
    this.isEditEnabled.off();
    this.reset();
  }

  async handleEditSave() {
    const { hasError } = await this.validate();
    if (hasError) return;
    this.$.date = new FieldState(this.$.date.$);
    this.$.amount = new FieldState(this.$.amount.$);
    this.$.exchangeRate = new FieldState(this.$.exchangeRate.$);
    this.isEditEnabled.off();
  }
}
